import { AppLink } from '@/components/ui/app-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Notice } from '@/components/ui/notice';
import { usePublishNow, usePublishStatus, useRemovePublishTarget } from '@/hooks';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { formatTimeAgo } from '@/utils/timeUtils';
import type { DrivePublishRunSummary, DrivePublishStatus } from '@vivport/backend/types';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmActionDialog } from '../integrations/ConfirmActionDialog';
import { SettingsRow, SettingsRowList, SettingsSection, SettingsSectionBody, SettingsStateLine } from '../SettingsPage';
import { PublishFolderDialog } from './PublishFolderDialog';

type PublishingCardProps = {
  memberId: number;
  isHubAdmin: boolean;
};

const ONE_WAY_COPY =
  'One-way: Vivport writes, it never reads these files back. Everything the configuring admin can see is published.';
const CONFLICT_EXPLANATION =
  'Edited in the drive; Vivport has stopped updating this file. Delete or rename it in the drive to let Vivport publish it again.';
const ROOT_MISSING_COPY = 'The Vivport folder was deleted or moved. Remove publishing and choose a folder again.';

function summaryCounters(summary: DrivePublishRunSummary): string {
  const parts = [
    `${summary.created} new`,
    `${summary.replaced} updated`,
    summary.moved > 0 ? `${summary.moved} moved` : null,
    summary.deleted > 0 ? `${summary.deleted} removed` : null,
    `${summary.conflicts} conflict${summary.conflicts === 1 ? '' : 's'}`,
    summary.leftInPlace > 0 ? `${summary.leftInPlace} left in place` : null,
    summary.skipped > 0 ? `${summary.skipped} skipped` : null,
    summary.errors.length > 0 ? `${summary.errors.length} error${summary.errors.length === 1 ? '' : 's'}` : null,
    `${summary.unchanged} unchanged`,
  ];
  return parts.filter((part): part is string => part !== null).join(', ');
}

function LastRunLine({ target }: { target: DrivePublishStatus }) {
  if (target.inFlight) {
    return <p className="text-sm text-muted-foreground">Publishing now...</p>;
  }
  if (target.lastRunFinishedAt === null) {
    return <p className="text-sm text-muted-foreground">Not run yet.</p>;
  }
  const when = `Last run ${formatTimeAgo(new Date(target.lastRunFinishedAt))}`;
  if (target.lastRunError !== null) {
    return (
      <p className="text-sm text-destructive">
        {when}: failed. {target.lastRunError}
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {when}: {target.lastRunSummary ? summaryCounters(target.lastRunSummary) : 'succeeded'}.
    </p>
  );
}

function ConfiguredBody({ target, isHubAdmin }: { target: DrivePublishStatus; isHubAdmin: boolean }) {
  const publishNow = usePublishNow();
  const removeTarget = useRemovePublishTarget();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const handlePublishNow = () => {
    publishNow.mutate(undefined, {
      onSuccess: () => toast.success('Publishing run started.'),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to start publishing.')),
    });
  };

  const handleRemove = () => {
    removeTarget.mutate(undefined, {
      onSuccess: (result) =>
        toast.success(`Publishing removed. ${result.itemsLeftInDrive} published item(s) stay in the drive.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to remove publishing.')),
    });
  };

  return (
    <>
      <SettingsSectionBody>
        {target.rootMissingAt !== null ? <Notice tone="warning">{ROOT_MISSING_COPY}</Notice> : null}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{target.driveName}</span>
            <Badge variant="outline">{target.productLabel}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Publish folder</span>
            <span className="font-mono text-xs">{target.rootPath}</span>
            {target.webUrl ? (
              <a
                href={target.webUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
          <LastRunLine target={target} />
          <p className="text-xs text-muted-foreground">
            {target.counts.published} file{target.counts.published === 1 ? '' : 's'} published
          </p>
        </div>
        {isHubAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handlePublishNow}
              disabled={target.inFlight || target.rootMissingAt !== null || publishNow.isPending}
            >
              {publishNow.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish now
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirmRemove(true)}
              disabled={removeTarget.isPending}
            >
              Remove publishing
            </Button>
          </div>
        ) : null}
      </SettingsSectionBody>
      {target.conflicts.length > 0 ? (
        <div className="border-t">
          <div className="px-4 pt-4 pb-1">
            <h4 className="text-sm font-medium">Conflicts</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">{CONFLICT_EXPLANATION}</p>
          </div>
          <SettingsRowList>
            {target.conflicts.map((conflict) => (
              <SettingsRow
                key={conflict.itemKey}
                truncate
                title={conflict.name}
                meta={
                  conflict.contentCode ? (
                    <AppLink to={toContentHref(conflict.contentCode)} className="text-xs text-primary hover:underline">
                      {conflict.contentCode}
                    </AppLink>
                  ) : null
                }
                detail={conflict.providerPath}
                trailing={
                  conflict.conflictAt ? (
                    <span className="text-xs text-muted-foreground">{formatTimeAgo(new Date(conflict.conflictAt))}</span>
                  ) : null
                }
              />
            ))}
          </SettingsRowList>
        </div>
      ) : null}
      <ConfirmActionDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remove publishing?"
        description="Vivport stops publishing to this folder. The published files stay in the drive; Vivport will no longer update them."
        confirmLabel="Remove publishing"
        onConfirm={handleRemove}
      />
    </>
  );
}

export function PublishingCard({ memberId, isHubAdmin }: PublishingCardProps) {
  const status = usePublishStatus();
  const [choosing, setChoosing] = useState(false);
  const target = status.data?.target ?? null;
  const showChoose = isHubAdmin && status.isSuccess && target === null;

  return (
    <SettingsSection
      title="Publishing"
      description={ONE_WAY_COPY}
      actions={
        showChoose ? (
          <Button type="button" size="sm" onClick={() => setChoosing(true)}>
            Choose folder
          </Button>
        ) : null
      }
    >
      {status.isLoading ? <SettingsStateLine>Loading publishing...</SettingsStateLine> : null}
      {status.isError ? (
        <SettingsStateLine tone="destructive">
          {getAxiosErrorDisplayMessage(status.error, 'Failed to load publishing.')}
        </SettingsStateLine>
      ) : null}
      {status.isSuccess && target === null ? (
        <SettingsSectionBody>
          <p className="text-sm text-muted-foreground">Publish this Hub&apos;s projects to a drive</p>
        </SettingsSectionBody>
      ) : null}
      {target !== null ? <ConfiguredBody target={target} isHubAdmin={isHubAdmin} /> : null}
      {isHubAdmin ? <PublishFolderDialog memberId={memberId} open={choosing} onOpenChange={setChoosing} /> : null}
    </SettingsSection>
  );
}
